import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, CardBody, Input, Table } from 'reactstrap';
import notify, { MESSAGES } from '../../utils/notify';
import logger from '../../utils/logger';
import ActionButton from '../../Components/Common/ActionButton';
import { EmptyState } from '../../Components/Common/ListStates';
import { SkeletonText } from '../../Components/Common/Skeleton';
import GtcViewer from '../../Components/Gtc/GtcViewer';
import gtcApi, { monthName } from '../Master/data/gtcApi';
import { pick } from '../../shared/offer-domain/pick';
import StepActionBar from './component/StepActionBar';
import './component/procurementDetail.css';

// Step 3: pick one or more GTC (Active + Publish only). The selection lives in
// form.gtcIds and is sent with the normal procurement save.
const GtcTab = ({
  goNext,
  goBack,
  form,
  setForm,
  handleSaveProc,
  handleSaveCompleteGtc,
}) => {
  const [options, setOptions] = useState(null);
  const [trees, setTrees] = useState({});
  const [error, setError] = useState(null);
  const requested = useRef(new Set());

  // BE rejects gtcIds changes once Tender Opening sent the invitation.
  const isInviteSent = form.isInviteDMUTCompleted === true;
  const isLocked = form.isProcurementCompleted !== false || isInviteSent;
  const isStepDone = form.isGtcCompleted === true;
  const selectedIds = useMemo(() => form.gtcIds || [], [form.gtcIds]);

  useEffect(() => {
    gtcApi
      .listSelectable()
      .then(data => setOptions(pick(data, 'items', 'Items') || []))
      .catch(err => {
        setOptions([]);
        notify.apiError(err, 'load the GTC list');
      });
  }, []);

  // Full trees for the preview, fetched once per selected GTC.
  useEffect(() => {
    selectedIds.forEach(id => {
      if (requested.current.has(id)) return;
      requested.current.add(id);
      gtcApi
        .getById(id)
        .then(tree => setTrees(prev => ({ ...prev, [id]: tree })))
        .catch(err => {
          // Allow a retry on the next selection change.
          requested.current.delete(id);
          logger.warn('GTC preview failed', err);
          notify.apiError(err, 'load the GTC preview');
        });
    });
  }, [selectedIds]);

  // GTC already attached stay listed even if no longer selectable.
  const rows = [
    ...(options || []),
    ...(form.gtcs || []).filter(
      gtc => !(options || []).some(option => option.id === gtc.id)
    ),
  ].filter(gtc => !isLocked || selectedIds.includes(gtc.id));

  const toggle = id => {
    setError(null);
    setForm(prev => {
      const current = prev.gtcIds || [];
      return {
        ...prev,
        gtcIds: current.includes(id)
          ? current.filter(item => item !== id)
          : [...current, id],
      };
    });
  };

  const handleComplete = async () => {
    if (!selectedIds.length) {
      setError('Please select at least one GTC.');
      notify.warning(MESSAGES.mandatory);
      return;
    }
    const completed = await handleSaveCompleteGtc();
    if (completed) goNext?.();
  };

  const previewGtcs = selectedIds.map(id => trees[id]).filter(Boolean);

  return (
    <React.Fragment>
      <div className='pd-step-head'>
        <div>
          <h5>
            <i className='ri-scales-3-line me-2'></i>
            GTC
          </h5>
          <p>
            Select the General Terms &amp; Conditions that apply to this
            procurement. Only Active and Published GTC can be selected.
          </p>
        </div>
        <div className='pd-counters' aria-live='polite'>
          <div className='pd-counter'>
            <strong>{selectedIds.length}</strong>
            <span>selected</span>
          </div>
        </div>
      </div>

      {isInviteSent && (
        <div className='pd-banner pd-banner--warn' role='status'>
          <i className='ri-lock-line' aria-hidden='true'></i>
          <div>
            The GTC list is locked because the invitation has been sent to
            Registered Suppliers.
          </div>
        </div>
      )}

      <Card className='border shadow-sm mb-3'>
        <CardBody>
          {error && (
            <div
              className='text-danger d-flex align-items-center gap-1 mb-2'
              style={{ fontSize: '12px' }}
              role='alert'
            >
              <i className='ri-error-warning-line' aria-hidden='true'></i>
              <span>{error}</span>
            </div>
          )}
          {options === null ? (
            <SkeletonText lines={4} />
          ) : rows.length === 0 ? (
            <EmptyState
              compact
              icon='ri-scales-3-line'
              title='No GTC available'
              emptyHint={
                isLocked
                  ? 'No GTC was selected for this procurement.'
                  : 'There is no Active and Published GTC yet. Publish one in Master GTC first.'
              }
            />
          ) : (
            <div className='table-responsive'>
              <Table className='align-middle table-nowrap mb-0' size='sm'>
                <thead className='table-light'>
                  <tr>
                    {!isLocked && (
                      <th className='text-center' style={{ width: '50px' }}>
                        <span className='visually-hidden'>Select</span>
                      </th>
                    )}
                    <th>GTC Number</th>
                    <th>Title</th>
                    <th>Period</th>
                    <th>Incoterm</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(gtc => (
                    <tr key={gtc.id}>
                      {!isLocked && (
                        <td className='text-center'>
                          <div className='form-check'>
                            <Input
                              className='form-check-input'
                              type='checkbox'
                              aria-label={`Select ${gtc.gtcNumber}`}
                              checked={selectedIds.includes(gtc.id)}
                              onChange={() => toggle(gtc.id)}
                            />
                          </div>
                        </td>
                      )}
                      <td className='fw-semibold'>{gtc.gtcNumber || '-'}</td>
                      <td className='text-wrap'>{gtc.title || '-'}</td>
                      <td>
                        {[monthName(gtc.month), gtc.year]
                          .filter(Boolean)
                          .join(' ') || '-'}
                      </td>
                      <td>{gtc.incotermName || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      <Card className='border shadow-sm mb-3'>
        <CardBody>
          <h6 className='text-uppercase fw-semibold mb-3 fs-12'>
            Preview of selected GTC
          </h6>
          <GtcViewer
            gtcs={previewGtcs}
            emptyText='Select at least one GTC to preview its clauses.'
          />
        </CardBody>
      </Card>

      <StepActionBar
        status={
          isInviteSent
            ? 'Locked after invitation was sent'
            : isLocked
              ? 'Read-only after submission'
              : 'Save to keep changes'
        }
        statusTone={isLocked ? 'muted' : 'ok'}
        statusIcon={isLocked ? 'ri-lock-line' : 'ri-information-line'}
      >
        <ActionButton
          variant='secondary'
          icon='ri-arrow-left-line'
          onClick={() => goBack?.()}
        >
          Supporting Documents
        </ActionButton>
        {!isLocked && (
          <ActionButton
            variant='secondary'
            icon='ri-save-line'
            onClick={handleSaveProc}
          >
            Save
          </ActionButton>
        )}
        {isLocked || isStepDone ? (
          <ActionButton
            variant='primary'
            icon='ri-arrow-right-line'
            onClick={() => goNext?.()}
          >
            Next: Supplier Invitation
          </ActionButton>
        ) : (
          <ActionButton
            variant='primary'
            icon='ri-check-double-line'
            onClick={handleComplete}
          >
            Complete &amp; Continue
          </ActionButton>
        )}
      </StepActionBar>
    </React.Fragment>
  );
};

GtcTab.propTypes = {
  goNext: PropTypes.func,
  goBack: PropTypes.func,
  form: PropTypes.object.isRequired,
  setForm: PropTypes.func.isRequired,
  handleSaveProc: PropTypes.func.isRequired,
  handleSaveCompleteGtc: PropTypes.func.isRequired,
};

export default GtcTab;
